/**
 * 「URLから読み取る」をほぼ全画面のモーダルで開く (docs/features/product-url-crawl-summary-plan.md §4.6)。
 * 案件の詳細 (閲覧・編集どちらからでも) と新規登録の両方で使う。「反映」でフォームへ入れたらモーダルを閉じる。
 *
 * - 閉じても読み取りはサーバー側で続く。もう一度開くと、パネルが引き継ぎ情報 (localStorage) から続きを表示する。
 *   読み取り中はそのことを × のツールチップで伝える
 * - 背景クリックでは閉じない (読み取り中・確認中の誤操作を避ける)。閉じるのは × と Esc だけ
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { SquareSparkles } from 'lucide-react';
import { ProductUrlSummaryPanel, type ProductUrlSummaryApply } from './ProductUrlSummaryPanel';
import type { UrlSummaryFormSnapshot, UrlSummaryNewBrandDraft } from '../../../features/products/urlSummary';
import './ProductUrlSummaryPanel.css';

/** 起動ボタンとモーダル見出しのアイコン */
export const UrlSummaryIcon = SquareSparkles;

interface ModalProps {
  open: boolean;
  onClose: () => void;
  productId?: string | null;
  /** 新規登録の下書き ID (引き継ぎ情報のキーにだけ使う) */
  draftId?: string | null;
  getForm: () => UrlSummaryFormSnapshot;
  brands: ReadonlyArray<{ id: string; brand_name: string }>;
  /** 採用した項目をフォームへ反映する (呼び出し後にモーダルは閉じる) */
  onApply: (apply: ProductUrlSummaryApply) => void;
  onCreateBrand?: (draft: UrlSummaryNewBrandDraft) => void;
  disabled?: boolean;
}

export function ProductUrlSummaryModal({ open, onClose, onApply, ...panelProps }: ModalProps) {
  const [running, setRunning] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="modal-overlay">
      <div className="modal-card url-summary-modal" role="dialog" aria-modal="true" aria-labelledby="url-summary-modal-title">
        <div className="modal-header">
          <h2 className="modal-title url-summary-modal-title" id="url-summary-modal-title">
            <UrlSummaryIcon size={18} aria-hidden="true" />
            URLから読み取る
          </h2>
          <span className="url-summary-close">
            <button
              type="button"
              className="modal-close-button"
              onClick={onClose}
              aria-label="閉じる"
              aria-describedby={running ? 'url-summary-close-tip' : undefined}
            >
              ×
            </button>
            {running && (
              <span className="url-summary-close-tip" id="url-summary-close-tip" role="tooltip">
                ページを離れても読み取りは続きます。戻ってきたときに続きから表示します
              </span>
            )}
          </span>
        </div>
        <div className="modal-body">
          <ProductUrlSummaryPanel
            {...panelProps}
            embedded
            onRunningChange={setRunning}
            onApply={(apply) => {
              onApply(apply);
              onClose();
            }}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}

interface LauncherProps {
  onClick: () => void;
  /** 処理中または結果が未確認の読み取りがある (続きがあることを示す) */
  pending?: boolean;
  disabled?: boolean;
}

/**
 * モーダルを開くボタン。続きがあるときは「続きを見る」にして印を付ける。
 * 案件ページの主役ではない AI 操作なので、枠はモノクロのグラデーション (btn-ai-director-mono・利用者指示 2026-10-30)
 */
export function UrlSummaryLauncher({ onClick, pending, disabled }: LauncherProps) {
  return (
    <button type="button" className="btn-ai-director-sm btn-ai-director-mono url-summary-launcher" onClick={onClick} disabled={disabled}>
      <UrlSummaryIcon size={14} aria-hidden="true" />
      <span className="btn-label">{pending ? 'URL読み取りの続きを見る' : 'URLから読み取る'}</span>
      {pending && <span className="url-summary-launcher-dot" aria-hidden="true" />}
    </button>
  );
}
